import { captureException } from '@/utils/sentry';
import { PaymentBatchStatus, PaymentHistoryStatus } from '@/enums';
import { Theme } from '@/contexts/theme-context';

const PAYMENT_BATCH_STATUS_MAP: Record<
  PaymentBatchStatus,
  PaymentHistoryStatus
> = {
  [PaymentBatchStatus.AUTHORISATION]: PaymentHistoryStatus.Success,
  [PaymentBatchStatus.AUTHORISATION_ADJUSTMENT]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.AUTHORISATION_ADJUSTMENT_FAILED]:
    PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.AUTHORISATION_FAILED]: PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.AUTORESCUE]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.CANCEL_AUTORESCUE]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.CANCEL_OR_REFUND]: PaymentHistoryStatus.FullRefund,
  [PaymentBatchStatus.CANCELLATION]: PaymentHistoryStatus.FullRefund,
  [PaymentBatchStatus.CAPTURE]: PaymentHistoryStatus.Success,
  [PaymentBatchStatus.CAPTURE_FAILED]: PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.CHARGEBACK]: PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.CHARGEBACK_REVERSED]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.CREATED]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.DUPLICATE]: PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.HANDLED_EXTERNALLY]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.MANUAL_REVIEW_ACCEPT]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.MANUAL_REVIEW_REJECT]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.NOTIFICATION_OF_CHARGEBACK]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.NOTIFICATION_OF_FRAUD]: PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.OFFER_CLOSED]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.ORDER_CLOSED]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.ORDER_OPENED]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.PAIDOUT_REVERSED]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.PARTIAL_REFUND]: PaymentHistoryStatus.PartialRefund,
  [PaymentBatchStatus.PAY_BY_LINK_CANCELLATION]:
    PaymentHistoryStatus.FullRefund,
  [PaymentBatchStatus.PAYOUT_DECLINE]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.PAYOUT_EXPIRE]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.PAYOUT_THIRDPARTY]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.PENDING_CAPTURE]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.POSTPONED_REFUND]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.PREARBITRATION_LOST]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.PREARBITRATION_WON]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.RECURRING_CONTRACT]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.REFUND]: PaymentHistoryStatus.FullRefund,
  [PaymentBatchStatus.REFUND_FAILED]: PaymentHistoryStatus.RefundFailed,
  [PaymentBatchStatus.REFUND_WITH_DATA]: PaymentHistoryStatus.FullRefund,
  [PaymentBatchStatus.REFUNDED_REVERSED]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.REPORT_AVAILABLE]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.REQUEST_FOR_INFORMATION]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.SECOND_CHARGEBACK]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.TECHNICAL_CANCEL]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.TERMINAL_CANCEL]: PaymentHistoryStatus.FullRefund,
  [PaymentBatchStatus.TERMINAL_COMMUNICATION_FAILED]:
    PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.TERMINAL_FAILED]: PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.TERMINAL_PRE_AUTH_PENDING]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.THREEDS_FAILED]: PaymentHistoryStatus.Failed,
  [PaymentBatchStatus.THREEDS_PENDING]: PaymentHistoryStatus.Pending,
  [PaymentBatchStatus.VOID_PENDING_REFUND]: PaymentHistoryStatus.RefundPending,
};

const getPaymentHistoryStatusColorTranslationMap = (
  theme: Theme,
): Record<PaymentHistoryStatus, [string, string]> => {
  return {
    [PaymentHistoryStatus.Expired]: [
      theme.colors.inactiveText,
      'payment_history.expired',
    ],
    [PaymentHistoryStatus.Failed]: [
      theme.colors.negative,
      'payment_history.failed',
    ],
    [PaymentHistoryStatus.PartialRefund]: [
      theme.colors.darkGreen,
      'payment_history.success',
    ],
    [PaymentHistoryStatus.Pending]: [theme.colors.caution, 'pending'],
    [PaymentHistoryStatus.Success]: [
      theme.colors.darkGreen,
      'payment_history.success',
    ],
    [PaymentHistoryStatus.FullRefund]: [
      theme.colors.inactiveText,
      'payment_history.full_refund',
    ],
    [PaymentHistoryStatus.RefundFailed]: [
      theme.colors.negative,
      'payment_history.refund_failed',
    ],
    [PaymentHistoryStatus.RefundPending]: [
      theme.colors.caution,
      'payment_history.refund_pending',
    ],
  };
};

const getSecondaryPaymentHistoryStatusColorTranslationMap = (
  theme: Theme,
): Partial<Record<PaymentHistoryStatus, [string, string]>> => {
  return {
    [PaymentHistoryStatus.PartialRefund]: [
      theme.colors.inactiveText,
      'payment_history.partial_refund',
    ],
  };
};

const isPaymentExpiredPreAuth = (
  createdAt: Date,
  status: PaymentBatchStatus,
) => {
  const preAuthExpirationDays = 14;
  const currentDate = new Date();
  const expirationDate = new Date(
    currentDate.setDate(currentDate.getDate() - preAuthExpirationDays),
  );

  return (
    status === PaymentBatchStatus.PENDING_CAPTURE && createdAt < expirationDate
  );
};

export const getPaymentHistoryStatus = (
  createdAt: Date,
  status: PaymentBatchStatus,
  theme: Theme,
): {
  secondaryStatusColor?: string;
  secondaryStatusText?: string;
  statusColor: string;
  statusText: string;
} => {
  try {
    const isExpired = isPaymentExpiredPreAuth(createdAt, status);
    const paymentHistoryStatus = isExpired
      ? PaymentHistoryStatus.Expired
      : PAYMENT_BATCH_STATUS_MAP[status];
    const [statusColor, statusText] =
      getPaymentHistoryStatusColorTranslationMap(theme)[paymentHistoryStatus];

    const [secondaryStatusColor, secondaryStatusText] =
      getSecondaryPaymentHistoryStatusColorTranslationMap(theme)[
        paymentHistoryStatus
      ] || [undefined, undefined];

    return {
      secondaryStatusColor,
      secondaryStatusText,
      statusColor,
      statusText,
    };
  } catch {
    captureException(new Error('Missing PaymentBatchStatus mapping'), {
      extra: {
        status: JSON.stringify(status),
      },
    });

    return {
      statusColor: theme.colors.inactiveText,
      statusText: 'pending',
    };
  }
};
